import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FloatingLabel, Form, Image } from "react-bootstrap";

import { useAuth } from "../contexts/AuthContext";
import Utils from "../configs/Utils";
import Enums from "../configs/Enums";

import "../assets/css/account.scss";
import Loading from "../Components/Common/Loading";

const Login = () => {
  const userRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, currentUser, setCurrentUser } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailErrMsg, setEmailErrMsg] = useState("");
  const [password, setPassword] = useState("");
  const [passwordErrMsg, setPasswordErrMsg] = useState("");

  const fromPathname = location.state?.from?.pathname;
  const fromSearch = location.state?.from?.search || "";
  const from = fromPathname ? fromPathname + fromSearch : "/";

  useEffect(() => {
    Utils.setPageTitle("Login page");
  }, []);

  useEffect(() => {
    userRef.current?.focus();

    // Redirect to activation page if account is not activated
    if (isAuthenticated) {
      if (currentUser?.status !== "activated") {
        navigate("/account/activate", { replace: true });
        return;
      }

      navigate(from, { replace: true });
    }
  }, [isAuthenticated, currentUser, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    if (!email.length) {
      setEmailErrMsg("This field is required!");
      valid = false;
    }
    if (!password.length) {
      setPasswordErrMsg("This field is required!");
      valid = false;
    }

    if (!valid) {
      return false;
    }

    setFormLoading(true);

    const data = await Utils._apiRequest({
      path: Enums.endpoints.auth.LOGIN,
      type: "POST",
      payload: { email, password },
    });

    if (data.errors) {
      if (data.errors && data.errors.email !== "") {
        setEmailErrMsg(data.errors.email);
      }
      if (data.errors && data.errors.password !== "") {
        console.log(data.errors.password);
        setPasswordErrMsg(data.errors.password);
      }
      setFormLoading(false);
    } else {
      // Server sets httpOnly cookie automatically, just update the user state
      setCurrentUser(data);
      if (data?.status !== "activated") {
        navigate("/account/activate", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  };

  const clearErrMessage = (type) => {
    if (type === "email") {
      setEmailErrMsg("");
    } else {
      setPasswordErrMsg("");
    }
  };

  return (
    <main className="page-main account-page container">
      <section className="login-wrapper">
        <Form
          className={`auth-form-shell p-4 p-md-5 border overflow-hidden shadow rounded-3 bg-body-tertiary${formLoading ? " loading" : ""}`}
          onSubmit={handleSubmit}
        >
          <Loading show={formLoading} />
          <h1 className="logo text-center">
            <Image
              src={Utils.getPublicImage("/assets/images/logo-main.png")}
              width={145}
              alt=""
            />
          </h1>

          <div className="auth-copy text-center">
            <span className="auth-kicker">Secure access</span>
            <h2>Welcome back</h2>
            <p>Manage stores, orders, and synchronized gateways from one focused workspace.</p>
          </div>

          <FloatingLabel
            controlId="emailFloatingInput"
            label="Email address"
            className="mb-3"
          >
            <Form.Control
              ref={userRef}
              type="email"
              className={emailErrMsg !== "" ? " is-invalid" : ""}
              placeholder="name@example.com"
              onChange={(e) => {
                setEmail(e.target.value);
                clearErrMessage("email");
              }}
            />

            {emailErrMsg !== "" ? (
              <p className="invalid-feedback">{emailErrMsg}</p>
            ) : null}
          </FloatingLabel>

          <FloatingLabel
            controlId="passwordFloatingInput"
            label="Password"
            className="mb-3"
          >
            <Form.Control
              type="password"
              className={passwordErrMsg !== "" ? " is-invalid" : ""}
              onChange={(e) => {
                setPassword(e.target.value);
                clearErrMessage("password");
              }}
              autoComplete="false"
              placeholder="password"
            />

            {passwordErrMsg !== "" ? (
              <p className="invalid-feedback">{passwordErrMsg}</p>
            ) : null}
          </FloatingLabel>

          <div className="auth-link-row mb-3 text-end">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>

          <button className="w-100 btn btn-lg btn-primary">Login</button>

          <div className="footer">
            <p className="mb-1">
              You don&lsquo;t have an account?{" "}
              <Link to="/register">Register</Link>
            </p>
            <p>
              By continuing, you agree to our{" "}
              <Link to="/policy">Privacy Policy</Link>.
            </p>
          </div>
        </Form>
      </section>
    </main>
  );
};

export default Login;
